import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, Check, Wand2, Sparkles } from "lucide-react";
import { api, img } from "../../lib/api";
import { act, PageHead, Badge } from "./kit";

const MODES = [["enhance", "Auto enhance + crop (white)"], ["enhance_dark", "Auto enhance + crop (dark)"], ["white", "AI white catalogue"], ["dark", "AI premium dark"], ["social", "AI social media"], ["studio", "AI bg removal + studio"]];

export default function Studio() {
  const nav = useNavigate();
  const [shots, setShots] = useState([]);
  const [cur, setCur] = useState(null);
  const [out, setOut] = useState(null);
  const [approved, setApproved] = useState([]);
  const [busy, setBusy] = useState("");
  const [hints, setHints] = useState("");
  const [sug, setSug] = useState(null);
  const upload = async (files) => {
    setBusy("upload");
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("kind", "product_image");
      const r = await act(() => api.post("/admin/files", fd));
      if (r) { setShots((s) => [...s, r]); setCur(r); }
    }
    setBusy("");
  };
  const run = async (mode) => {
    setBusy(mode);
    setOut(null);
    const r = await act(() => api.post("/admin/studio/process", { path: cur.path, mode }, { timeout: 180000 }), "Processed — review before approving");
    if (r) setOut(r);
    setBusy("");
  };
  const describe = async () => {
    setBusy("describe");
    const paths = (approved.length ? approved : shots).slice(0, 4).map((x) => x.path);
    const r = await act(() => api.post("/admin/studio/describe", { paths, hints }, { timeout: 120000 }));
    if (r) setSug(r);
    setBusy("");
  };
  const createProduct = () => {
    const imgs = (approved.length ? approved : shots).map((x) => ({ url: x.url, alt: sug?.alt_text || sug?.title || "" }));
    sessionStorage.setItem("ddv_studio_draft", JSON.stringify(sug ? {
      title: sug.title, title_bm: sug.title_bm, description: sug.description, description_bm: sug.description_bm, category: sug.category, subcategory: sug.subcategory || "", brand: sug.brand_guess || "", images: imgs, status: "draft",
      tags: sug.seo_keywords || [], seo: { title: sug.seo_title, description: sug.seo_description, keywords: sug.seo_keywords },
      variants: (sug.suggested_sizes?.length ? sug.suggested_sizes : [""]).map((s) => ({ color: sug.colors?.[0] || "", size: s, sku: "", purchase_cost: 0, shipping_cost: 0, import_cost: 0, other_cost: 0, stock: 0, min_stock: 1 })),
    } : { images: imgs, status: "draft" }));
    nav("/admin/products/new");
  };
  return (
    <div data-testid="admin-studio">
      <PageHead title="AI Product Studio" sub="Capture → process → approve → generate listing. AI must keep real logos, stitching, colours, condition and defects.">
        <label className="btn-gold cursor-pointer" data-testid="studio-camera-btn"><Camera className="h-4 w-4" />{busy === "upload" ? "Uploading…" : "Take / upload photos"}
          <input type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={(e) => upload(e.target.files)} data-testid="studio-file-input" /></label>
      </PageHead>
      <div className="flex gap-2 overflow-x-auto pb-2">{shots.map((s) => <button key={s.path} onClick={() => { setCur(s); setOut(null); }} className={`shrink-0 border-2 ${cur?.path === s.path ? "border-[#C8A45D]" : "border-transparent"}`}><img src={img(s.url)} alt="shot" className="h-20 w-20 object-cover" /></button>)}</div>
      {cur && (
        <div className="mt-4 grid gap-6 lg:grid-cols-[1fr_1fr_260px]">
          <div><p className="eyebrow mb-2">Before</p><img src={img(cur.url)} alt="before" className="aspect-square w-full bg-[#141414] object-contain" data-testid="studio-before-img" /></div>
          <div><p className="eyebrow mb-2">After {out && <Badge s={out.engine} />}</p>
            {out ? <img src={img(out.url)} alt="after" className="aspect-square w-full bg-[#141414] object-contain" data-testid="studio-after-img" /> : <div className="grid aspect-square w-full place-items-center border border-dashed border-white/15 text-sm text-zinc-500">{busy ? "Processing… AI modes can take up to a minute" : "Choose a treatment"}</div>}
            {out && <div className="mt-3 flex gap-2"><button onClick={() => { setApproved((a) => [...a, out]); setOut(null); }} className="btn-gold" data-testid="studio-approve-btn"><Check className="h-4 w-4" />Approve</button><button onClick={() => setOut(null)} className="btn-ghost" data-testid="studio-reject-btn">Reject</button></div>}
          </div>
          <div className="space-y-2">{MODES.map(([m, label]) => <button key={m} disabled={!!busy} onClick={() => run(m)} className="btn-ghost w-full justify-start px-3 py-2 text-xs" data-testid={`studio-mode-${m}`}><Wand2 className="h-3 w-3" />{busy === m ? "Working…" : label}</button>)}
            <button onClick={() => setApproved((a) => [...a, cur])} className="w-full text-left text-xs text-zinc-400 underline" data-testid="studio-approve-original-btn">Approve original as-is</button></div>
        </div>
      )}
      {approved.length > 0 && <div className="mt-8"><p className="eyebrow mb-2">Approved images ({approved.length})</p><div className="flex flex-wrap gap-2" data-testid="studio-approved-list">{approved.map((a, i) => <img key={i} src={img(a.url)} alt="approved" className="h-24 w-24 object-cover" />)}</div></div>}
      {shots.length > 0 && (
        <div className="mt-8 border border-white/10 p-4">
          <p className="eyebrow mb-2">AI listing assistant</p>
          <div className="flex flex-col gap-2 sm:flex-row"><input value={hints} onChange={(e) => setHints(e.target.value)} placeholder="Hints: brand, season, size, known defects, receipt info…" className="field" data-testid="studio-hints-input" />
            <button onClick={describe} disabled={!!busy} className="btn-gold" data-testid="studio-describe-btn"><Sparkles className="h-4 w-4" />{busy === "describe" ? "Thinking…" : "Generate details"}</button></div>
          {sug && (
            <div className="mt-4 grid gap-3 text-sm md:grid-cols-2" data-testid="studio-suggestions">
              {Object.entries(sug).map(([k, v]) => <div key={k}><p className="font-mono text-[10px] uppercase text-zinc-500">{k.replace(/_/g, " ")}</p><p className="text-zinc-200">{Array.isArray(v) ? v.join(", ") : String(v)}</p></div>)}
            </div>
          )}
          <button onClick={createProduct} className="btn-gold mt-4" data-testid="studio-create-product-btn">Create product draft from studio</button>
        </div>
      )}
    </div>
  );
}
